<script lang="ts" setup>
    import { onMounted, ref, reactive, computed} from 'vue';
    import * as ReportModel from '../../lib/Models/Reports/FinancialReportModel';
    import vSelect from 'vue-select'; 
    import * as _ from 'lodash';
    import { PageTitle } from '../../LibMain';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import { NoTzDate, getNoTzToday, addDay, toNoTzDate, toDate, toDisplayDateString, startOfMonth, addMonth, 
        endOfMonth, getDateDiffInDays, prettyDateTime } from '../../lib/DateUtils';
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import Tab from '../../components/Tab.vue';
    import SectionPanel from '../../components/SectionPanel.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import { ActionButtonStyle } from '../../lib/Types/Types'; 
    import { popAlert } from '../../lib/Notification';
    import { DateDisplayFormat, ButtonDisplaySize, LayoutOrientation } from "../../Enums/UiEnums";
    import * as ChoiceApi from '../../lib/ChoiceApi';
    import FinancialReportDailySummary from '../../ChoiceScreens/Reports/FinancialReportDailySummary.vue';
    import FinancialReportMonthlySummary from '../../ChoiceScreens/Reports/FinancialReportMonthlySummary.vue';
    import Currency from '../../components/Formatting/Currency.vue';

    import '../../../css/choice.css';
    import 'vue-select/dist/vue-select.css';

    const props = defineProps<{
        screenName: string,
        reportType: ReportModel.FinancialReportType,
        lookupUsers: ReportModel.IUserList[],
        lookupRenderingProvider: ReportModel.ILookupList[],
        lookupFinancialClass: ReportModel.ILookupList[],
        lookupPayer: ReportModel.IPayerList[],
        lookupServiceLocation: ReportModel.ILookupList[],
        lookupCpt: ReportModel.ILookupList[]
    }>();

    const emit = defineEmits<{
        (e: "onChangeTab", selectedTab: any): void
    }>();

    const reportData = reactive<{        
        tabName: ReportModel.FinancialTabName,
        reportRunDate?: NoTzDate,
        monthlyReportStartDate?: NoTzDate,
        monthlyReportEndDate?: NoTzDate,
        financialData?: ReportModel.IFinancialReportData,
        financialDataMonthly?: ReportModel.IFinancialReportDataMonthly,
        summaryData?: ReportModel.IFinancialReportResults[] | any[],
        mainDataMonthly?: ReportModel.IFinancialReportGroupedResults[],
        submittedClaimsData?: ReportModel.IFinancialReportResults[],
        submittedClaimsDataMonthly?: ReportModel.IFinancialReportGroupedResults[],
        summaryInputData: ReportModel.IFinancialReportInputs,
        summaryInputDataMonthly: ReportModel.IFinancialReportInputsMonthly,
        summaryStats: ReportModel.ISummaryStats
        showAdvancedSearchCriteria: boolean,
        advancedSearchCriteria: ReportModel.IAdvancedSearchCriteria,
        showSummaryReport: boolean,
        summarizedReportData?: ReportModel.IFinancialReportResults[] | any[]
        mainReportData?: ReportModel.IFinancialReportResults[] | any[],
        monthlyFinReportDwInfo?: ReportModel.IMonthlyFinancialDwInfo
        
        totalFields:
        {
            totalClaimCount?: number,
            totalCharges?: number,
            totalPatientPayments?: number,
            totalInsurancePayments?: number,
            totalUnappliedPayments?: number,
            totalRefundPayments?: number,
            totalPayments?: number,
            totalAdjustments?: number
        },
        
        showDrillDown: boolean,
        detailRowItem?: any
    }>({        
        tabName: props.reportType == ReportModel.FinancialReportType.Daily ? ReportModel.FinancialTabName.DailySummary : ReportModel.FinancialTabName.MonthlySummary,
        reportRunDate: getNoTzToday(),
        monthlyReportStartDate: toNoTzDate(startOfMonth(new Date())),
        monthlyReportEndDate: getNoTzToday(),

        totalFields:
        {
            totalClaimCount: 0,
            totalCharges: 0,
            totalPatientPayments: 0,
            totalInsurancePayments: 0,
            totalUnappliedPayments: 0,
            totalRefundPayments: 0,
            totalPayments: 0,
            totalAdjustments: 0
        },

        summaryInputData: 
            {
                searchDate: toDate(getNoTzToday()),
                searchDateType: ReportModel.FinancialReportDateTypes.PostDate
            },
        summaryInputDataMonthly:
            {
                searchDateStart: startOfMonth(new Date()),
                searchDateEnd: toDate(getNoTzToday()),
                searchDateType: ReportModel.FinancialReportDateTypes.PostDate,
                selectedGroupBy1: "primaryPayerFinancialClassDesc",
                selectedGroupBy2: "primaryPayerName",
                selectedCPTCode: [],
                selectedUserIds: [],
                selectedPrimaryPayerClassIds: [],
                selectedPrimaryPayerIds: [],
                selectedRenderingProviderIds: [],
                selectedServiceLocationIds: []
            },

        summaryStats:
            {
                claimsCreatedCount: 0,
                claimsCreatedAmount: 0,
                insurancePaymentCount: 0,
                insurancePaymentAmount: 0,
                patientPaymentCount: 0,
                patientPaymentAmount: 0,
                claimsSubmittedCount: 0,
                claimsSubmittedAmount: 0,
                rejectedClaimCount: 0,
                rejectedClaimAmount: 0
            },
        showAdvancedSearchCriteria: true,   
        advancedSearchCriteria:
            {
                dateTypeList: 
                    [
                        { dropDownValue: "PostDate", dropDownDescription: "Post Date" }, 
                        { dropDownValue: "ServiceDepositDate", dropDownDescription: "Service/Deposit Date" },
                        { dropDownValue: "SubmissionDate", dropDownDescription: "Submission Date" }
                    ],
                selectedDateType: ReportModel.FinancialReportDateTypes.PostDate,  
                primaryPayerClassList: [],
                selectedPrimaryPayerClassIds: [],
                renderingProviderList: [],
                selectedRenderingProviderIds: [],
                primaryPayerList: [],
                selectedPrimaryPayerIds: [],
                serviceLocationList: [],
                selectedServiceLocationIds: [],
                groupByDropDownList: 
                    [{columnName: "cptCode", dropDownDescription: "CPT Code"}, 
                    {columnName: "cptDesc", dropDownDescription: "CPT with Description"},
                    {columnName: "formattedDateOfService", dropDownDescription: "DOS Date"}, 
                    {columnName: "formattedDosYearMonth", dropDownDescription: "DOS Month/Year"}, 
                    {columnName: "serviceLocationName", dropDownDescription: "Service Location"}, 
                    {columnName: "primaryPayerName", dropDownDescription: "Primary Payer"}, 
                    {columnName: "primaryPayerFinancialClassDesc", dropDownDescription: "Primary Payer Class"}, 
                    {columnName: "paymentMethod", dropDownDescription: "Payment Method"}, 
                    {columnName: "postDay", dropDownDescription: "Post Day"},  
                    {columnName: "postUserIdChargeOrPayment", dropDownDescription: "Post User"},
                    {columnName: "renderingProviderName", dropDownDescription: "Rendering Provider"}
                ],
                selectedGroupBy1: "primaryPayerFinancialClassDesc",
                selectedGroupBy2: "primaryPayerName",
                postUserList: [],
                selectedPostUserIds: [],
                cptCodeList: [],
                selectedCptCodeIds: [],
                booleanList: [{ booleanValue: true, dropDownDescription: "Yes" }, { booleanValue: false, dropDownDescription: "No" }], 
                selectedIncludeRefunds: true,
                selectedIncludeCharges: true,
                selectedIncludeCounts: true,
                selectedIncludePaymentsAdjustments: true
            },
        showSummaryReport: false,
        showDrillDown: false
    });

    onMounted(async () => {         
        const lookupAllValue = -999;

        reportData.advancedSearchCriteria.primaryPayerClassList = props.lookupFinancialClass.filter(item => +item.encode != lookupAllValue);
        reportData.advancedSearchCriteria.renderingProviderList = props.lookupRenderingProvider.filter(item => +item.encode != lookupAllValue);
        reportData.advancedSearchCriteria.primaryPayerList = props.lookupPayer;
        reportData.advancedSearchCriteria.serviceLocationList = props.lookupServiceLocation.filter(item => +item.encode != lookupAllValue);
        reportData.advancedSearchCriteria.postUserList = _.orderBy(props.lookupUsers, 'userText');
        reportData.advancedSearchCriteria.cptCodeList = _.orderBy(props.lookupCpt.filter(item => +item.encode != lookupAllValue), 'encode');

        if (ReportModel.FinancialReportType.Daily == props.reportType) {
            await LoadDailyFinancialDataForSummary();
        }
        else {
            await LoadMonthlyFinancialDataForSummary();
        }        

    });

    const runReportDisabled = computed<boolean>(() => 
        (undefined == reportData.advancedSearchCriteria.selectedDateType || 
        undefined == reportData.advancedSearchCriteria.selectedGroupBy1 || 
        undefined == reportData.advancedSearchCriteria.selectedIncludeRefunds ||
        undefined == reportData.advancedSearchCriteria.selectedIncludeCharges ||
        undefined == reportData.advancedSearchCriteria.selectedIncludeCounts ||
        undefined == reportData.advancedSearchCriteria.selectedIncludePaymentsAdjustments));

    const runMonthlyReportDisabled = computed<boolean>(() => 
        (undefined == reportData.monthlyReportStartDate ||
        undefined == reportData.monthlyReportEndDate ||
        undefined == reportData.advancedSearchCriteria.selectedDateType || 
        undefined == reportData.advancedSearchCriteria.selectedGroupBy1 || 
        undefined == reportData.advancedSearchCriteria.selectedIncludeRefunds ||
        undefined == reportData.advancedSearchCriteria.selectedIncludeCharges ||
        undefined == reportData.advancedSearchCriteria.selectedIncludeCounts ||
        undefined == reportData.advancedSearchCriteria.selectedIncludePaymentsAdjustments));

    const reportDescription = computed(() => {
        return props.reportType == ReportModel.FinancialReportType.Daily ? "Daily Summary" : "Monthly Summary";
    });  

    const reportTitle = computed(() => {
        return props.reportType == ReportModel.FinancialReportType.Daily ? "Daily Financial Report" : "Monthly Financial Report";
    });  

    const monthlyReportRunDate = computed(() => {
        const isDwEnabled = reportData.monthlyFinReportDwInfo?.monthlyFinReport_UseDwVersion ?? false;
        const dwLabel = `Data Warehouse updated as of ${prettyDateTime(reportData.monthlyFinReportDwInfo?.monthlyFinReport_DwLastRunDate)}`;
        const rtLabel = `Real Time run on ${prettyDateTime(new Date())}`;
        const runDateLabel = `Source: ${isDwEnabled ? dwLabel : rtLabel}`;
        return props.reportType == ReportModel.FinancialReportType.Monthly? runDateLabel : "";
    });  


    async function printDailySummary() {
        try{
            document.title = 'Daily Financial Report.pdf';
            window.print();
        }
        catch(e){
            console.log("Error printing report: " + e);
        }
    }

    async function exportSummary() {
        try {    
            const newSummaryData: any = {};                

            if (props.reportType === ReportModel.FinancialReportType.Monthly) {
                newSummaryData["Monthly Start Date"] = reportData.monthlyReportStartDate;
                newSummaryData["Monthly End Date"] = reportData.monthlyReportEndDate;
            }
            else {
                newSummaryData["Daily Summary Date"] = reportData.reportRunDate;
            }

            //Add the remainder of the columns that are common to both Daily and Monthly reports
            newSummaryData["Insurance Payment Amount"] = reportData.summaryStats.insurancePaymentAmount;
            newSummaryData["Insurance Payment Count"] = reportData.summaryStats.insurancePaymentCount;
            newSummaryData["Patient Payment Amount"] = reportData.summaryStats.patientPaymentAmount;
            newSummaryData["Patient Payment Count"] = reportData.summaryStats.patientPaymentCount;
            newSummaryData["Claims Created Amount"] = reportData.summaryStats.claimsCreatedAmount;
            newSummaryData["Claims Created Count"] = reportData.summaryStats.claimsCreatedCount;
            newSummaryData["Claims Submitted Amount"] = reportData.summaryStats.claimsSubmittedAmount;
            newSummaryData["Claims Submitted Count"] = reportData.summaryStats.claimsSubmittedCount;

            //Now add the exclusively monthly columns
            if (props.reportType === ReportModel.FinancialReportType.Monthly) {
                newSummaryData["Rejected Claim Amount"] = reportData.summaryStats.rejectedClaimAmount;
                newSummaryData["Rejected Claim Count"] = reportData.summaryStats.rejectedClaimCount;
                newSummaryData["Unapplied Amount"] = reportData.summaryStats.unappliedAmount;
                newSummaryData["Unapplied Count"] = reportData.summaryStats.unappliedCount;
            }

            //This is shorthand to map the json items to an array
            //Only have 1 item, but json_to_sheet requires an array
            const summaryTab: [string, any][] = Object.entries(newSummaryData);        
            
            //Only show a subset of our data for the DailyDetails tab
            const dailyDetailData = reportData.summaryData?.map(item => ({
                    "Claim Id": item.claimId,
                    "Claim Status": item.claimStatus,
                    "Claim DOS": item.formattedDateOfService,
                    "Claim Submission Date": toDisplayDateString(item.lastClaimSubmissionDate, DateDisplayFormat.NumericDate),
                    "Chart": item.patientId,
                    "Patient Name": item.patientName,
                    "Primary Payer": item.primaryPayerName,
                    "Secondary Payer": item.secondaryPayerName,
                    "Tertiary Payer": item.tertiaryPayerName,
                    "Service Location": item.serviceLocationName,
                    "Rendering Provider": item.renderingProviderName,
                    "Charges": item.chargeAmount,
                    "Patient Payments": item.patientPaymentTotal,
                    "Insurance Payments": item.insurancePaymentTotal,
                    "Total Payments": item.paymentTotal,
                    "Total Adjustments": item.adjustmentTotal
                }));

            const wb = XLSX.utils.book_new()
            const ws1 = XLSX.utils.json_to_sheet(summaryTab, {skipHeader: 1});  //no idea how to remove error/warning on XLSX, Legacy?
            XLSX.utils.book_append_sheet(wb, ws1, props.reportType === ReportModel.FinancialReportType.Daily ? "DailySummary" : "MonthlySummary");
            
            //only export Detail tab if on the Daily report.  Too much data to bring down for monthly detail.
            if (props.reportType === ReportModel.FinancialReportType.Daily) {
                const ws2 = XLSX.utils.json_to_sheet(dailyDetailData);
                XLSX.utils.book_append_sheet(wb, ws2, "DailyDetails");
            }
            
            XLSX.writeFile(wb, props.reportType === ReportModel.FinancialReportType.Daily ? "DailyFinancialReport.xls" : "MonthlyFinancialReport.xls");
        }
        catch (e) {
            await popAlert("Error downloading data to Excel: " + e);
        }	
    }

    async function getReportThisMonth() {
        reportData.monthlyReportStartDate = toNoTzDate(startOfMonth(new Date()));
        reportData.monthlyReportEndDate = getNoTzToday();
        //Rather than automatically running since dataset can be large, will force user to click Run Report button to trigger instead
        reportData.showAdvancedSearchCriteria = true;
    }

    async function getReportToday() {
        reportData.reportRunDate = getNoTzToday();
        LoadDailyFinancialDataForSummary();
    }

    async function subtractReportMonth() {
        const tempDateFrom = toDate(reportData.monthlyReportStartDate);  
        reportData.monthlyReportStartDate = toNoTzDate(startOfMonth(addMonth(tempDateFrom ?? new Date(), -1)));  
        const tempDateTo = toDate(reportData.monthlyReportStartDate);  
        reportData.monthlyReportEndDate = toNoTzDate(endOfMonth(tempDateTo));
        reportData.showAdvancedSearchCriteria = true;
    }

    async function subtractReportDay() {
        const tempDate = toDate(reportData.reportRunDate);  
        reportData.reportRunDate = toNoTzDate(addDay(tempDate ?? new Date(), -1));  
        LoadDailyFinancialDataForSummary();
    }

    async function addReportMonth() {
        const tempDateFrom = toDate(reportData.monthlyReportStartDate);  
        reportData.monthlyReportStartDate = toNoTzDate(startOfMonth(addMonth(tempDateFrom ?? new Date(), 1)));  
        const tempDateTo = toDate(reportData.monthlyReportStartDate);  
        reportData.monthlyReportEndDate = toNoTzDate(endOfMonth(tempDateTo));
        reportData.showAdvancedSearchCriteria = true;
    }

    async function addReportDay() {
        const tempDate = toDate(reportData.reportRunDate);  
        reportData.reportRunDate = toNoTzDate(addDay(tempDate ?? new Date(), 1));
        LoadDailyFinancialDataForSummary();
    }

    function formatYearMonthDate(dateString: string) {
        const date = new Date(dateString);
        const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        return formattedDate;
    }

    async function LoadDailyFinancialDataForSummary() {
        reportData.summaryInputData.searchDate = toDate(reportData.reportRunDate);
        reportData.summaryInputData.searchDateType = ReportModel.FinancialTabName.DailySummary == reportData.tabName ? ReportModel.FinancialReportDateTypes.PostDate : reportData.advancedSearchCriteria.selectedDateType;
        const resp = await ChoiceApi.reporting.dailyFinancials.getDailyFinancialReportData(reportData.summaryInputData);

        if (!resp) {
            await popAlert("There was an error pulling the summary data on the Daily Financial Report", "Daily Financial Report");
            return;
        }

        reportData.financialData = resp;
        reportData.summaryData = reportData.financialData?.dailyFinancialData;
        reportData.submittedClaimsData = reportData.financialData?.submittedClaimData;

        //Now determine which data set we load into the mainReportData collection 
        /*
        Note, these SearchDateTypes are pulling 2 different ways.  
	    1. If Search Date Type is ServiceDepositDate or PostDate, we pull charges using either from_date or post_date, and payments using either payment_date 
           or post_date.  So this is more what is the transaction history on a given date, the charges don't necessarily correlate to the payments.
	    2. If Search Date Type is SubmissionDate, then we pull all claims submitted on that date.  Then we pull those claim charges and the payments made against 
           those claims.  So with this one, the payments do correlate to the charges being displayed.
        */
        reportData.mainReportData = (reportData.advancedSearchCriteria.selectedDateType == ReportModel.FinancialReportDateTypes.SubmissionDate) ? reportData.submittedClaimsData : reportData.summaryData;

        //let's map some new properties that we'll use with grouping
        reportData.mainReportData = reportData.mainReportData?.map(item => ({
            ...item,
            formattedPaymentPostDate: null !== item.paymentPostDate ? toDisplayDateString(item.paymentPostDate, DateDisplayFormat.NumericDate) : null,
            formattedDateOfService: null !== item.dateOfService ? toDisplayDateString(item.dateOfService, DateDisplayFormat.NumericDate): null,
            formattedDosYearMonth: null !== item.dateOfService ? formatYearMonthDate(item.dateOfService) : null,
            postUserIdChargeOrPayment: item.chargeAmount !== 0 ? item.claimCreatorId : item.paymentUserId
        }));

        ////////////////////////////////////////
        //Now calc the daily summary stats
        ////////////////////////////////////////
        //Get the payment-related data
        reportData.summaryStats.patientPaymentCount = _.sumBy(reportData.summaryData, 'patientPaymentCount');
        reportData.summaryStats.patientPaymentAmount = _.sumBy(reportData.summaryData, 'patientPaymentTotal');
        reportData.summaryStats.insurancePaymentCount = _.sumBy(reportData.summaryData, 'insurancePaymentCount');
        reportData.summaryStats.insurancePaymentAmount = _.sumBy(reportData.summaryData, 'insurancePaymentTotal');

        //get Claims Created data
        const dtSearch = new Date(reportData.reportRunDate ?? getNoTzToday()); 
        const formattedSearchDt = dtSearch.toISOString().slice(0, 10);  //format our search date to yyyy-mm-dd

        const claimsCreated = reportData.summaryData?.filter(item => {
            if (!item.chargePostDate) {
                return false;   //Filter out items with null or undefined chargePostDate
            }

            const date = new Date(item.chargePostDate);
            return date.toISOString().startsWith(formattedSearchDt);
        });

        const uniqueClaims = _.uniqBy(claimsCreated, 'claimId');  //get the unique claim id's, since I have detail at the cpt level here
        reportData.summaryStats.claimsCreatedCount = _.size(uniqueClaims);
        reportData.summaryStats.claimsCreatedAmount = _.sumBy(claimsCreated, 'chargeAmount');

        //get Claims Submitted data
        const uniqueClaimsSubmitted = _.uniqBy(reportData.submittedClaimsData, 'claimId');    
        reportData.summaryStats.claimsSubmittedCount = _.size(uniqueClaimsSubmitted);
        reportData.summaryStats.claimsSubmittedAmount = _.sumBy(reportData.submittedClaimsData, 'chargeAmount');

        //////////////////////////////////////////////////////////
        //Now summarize/filter for Adv Search Summary Report
        //////////////////////////////////////////////////////////
        //let's sum up for our 2 group by items
        if (undefined !== reportData.mainReportData)
        {
            //let's filter data based on user's search criteria selections
            const filteredResults: any = computed(() => {
                return reportData.mainReportData?.filter(item => 
                    (reportData.advancedSearchCriteria.selectedServiceLocationIds.length === 0 || reportData.advancedSearchCriteria.selectedServiceLocationIds.includes(item.serviceLocationEntityId)) &&
                    (reportData.advancedSearchCriteria.selectedPrimaryPayerIds.length === 0 || reportData.advancedSearchCriteria.selectedPrimaryPayerIds.includes(item.primaryPayerEntityId)) &&
                    (reportData.advancedSearchCriteria.selectedPrimaryPayerClassIds.length === 0 || reportData.advancedSearchCriteria.selectedPrimaryPayerClassIds.includes(item.primaryPayerFinancialClassId)) &&
                    (reportData.advancedSearchCriteria.selectedRenderingProviderIds.length === 0 || reportData.advancedSearchCriteria.selectedRenderingProviderIds.includes(item.renderingProviderEntityId)) &&                    
                    (reportData.advancedSearchCriteria.selectedPostUserIds.length === 0 || reportData.advancedSearchCriteria.selectedPostUserIds.includes(item.paymentUserId)) && 
                    (reportData.advancedSearchCriteria.selectedCptCodeIds.length === 0 || reportData.advancedSearchCriteria.selectedCptCodeIds.includes(item.cptCode)) 
                );
            });  

            let groupedWithCountAndSort = dynamicGroupByWithCountAndSort(filteredResults.value, reportData.advancedSearchCriteria.selectedGroupBy1, reportData.advancedSearchCriteria.selectedGroupBy2);
            reportData.summarizedReportData = groupedWithCountAndSort;

            //calculate total values in summarized collection
            reportData.totalFields.totalClaimCount = _.sumBy(reportData.summarizedReportData, 'claimCount');
            reportData.totalFields.totalCharges = _.sumBy(reportData.summarizedReportData, 'chargeAmount');
            reportData.totalFields.totalPatientPayments = _.sumBy(reportData.summarizedReportData, 'patientPaymentTotal');
            reportData.totalFields.totalInsurancePayments = _.sumBy(reportData.summarizedReportData, 'insurancePaymentTotal');
            reportData.totalFields.totalUnappliedPayments = _.sumBy(reportData.summarizedReportData, 'unappliedAmount');
            reportData.totalFields.totalRefundPayments = _.sumBy(reportData.summarizedReportData, 'refundTotal');
            reportData.totalFields.totalPayments = _.sumBy(reportData.summarizedReportData, 'paymentTotal');
            reportData.totalFields.totalAdjustments = _.sumBy(reportData.summarizedReportData, 'adjustmentTotal');
        }     

        //show the report on the Adv Search tab too
        reportData.showSummaryReport = true; 
    }

    async function LoadMonthlyFinancialDataForSummary() {
        //make sure user is only selecting a month date range at a time
        const ageDiff = getDateDiffInDays(toDate(reportData.monthlyReportStartDate)!, toDate(reportData.monthlyReportEndDate)!);

        if (ageDiff > 31) {
            await popAlert(`Your date range cannot exceed 31 days due to transactional volume.  It's currently at ${ageDiff} days.`, "Monthly Financial Report Date Range Exceeded");
            return;
        }

        reportData.summaryInputDataMonthly.searchDateStart = toDate(reportData.monthlyReportStartDate);
        reportData.summaryInputDataMonthly.searchDateEnd = toDate(reportData.monthlyReportEndDate);
        reportData.summaryInputDataMonthly.searchDateType = ReportModel.FinancialTabName.MonthlySummary == reportData.tabName ? ReportModel.FinancialReportDateTypes.PostDate : reportData.advancedSearchCriteria.selectedDateType;
        reportData.summaryInputDataMonthly.selectedGroupBy1 = reportData.advancedSearchCriteria.selectedGroupBy1;
        reportData.summaryInputDataMonthly.selectedGroupBy2 = reportData.advancedSearchCriteria.selectedGroupBy2;
        reportData.summaryInputDataMonthly.selectedCPTCode = reportData.advancedSearchCriteria.selectedCptCodeIds;
        reportData.summaryInputDataMonthly.selectedUserIds = reportData.advancedSearchCriteria.selectedPostUserIds;
        reportData.summaryInputDataMonthly.selectedPrimaryPayerClassIds = reportData.advancedSearchCriteria.selectedPrimaryPayerClassIds;
        reportData.summaryInputDataMonthly.selectedPrimaryPayerIds = reportData.advancedSearchCriteria.selectedPrimaryPayerIds;
        reportData.summaryInputDataMonthly.selectedRenderingProviderIds = reportData.advancedSearchCriteria.selectedRenderingProviderIds;
        reportData.summaryInputDataMonthly.selectedServiceLocationIds = reportData.advancedSearchCriteria.selectedServiceLocationIds;
        const resp = await ChoiceApi.reporting.monthlyFinancials.getMonthlyFinancialReportData(reportData.summaryInputDataMonthly);

        if (!resp) {
            await popAlert("There was an error pulling the summary data on the Monthly Financial Report", "Monthly Financial Report");
            return;
        }

        reportData.financialDataMonthly = resp;
        reportData.mainDataMonthly = resp.monthlyGroupedFinancialData;
        reportData.submittedClaimsDataMonthly = resp.monthlyGroupedSubmittedClaimData;
        reportData.summaryStats = resp.monthlySummaryStats;
        reportData.monthlyFinReportDwInfo = resp.monthlyFinReportDwInfo;
    }

    interface GroupResult {
        [key: string]: any; 
    }

    function dynamicGroupByWithCountAndSort(collection: any[], groupBy1: string, groupBy2?: string): GroupResult[] {
        const grouped = _.groupBy(collection, (item) => {
            let key = item[groupBy1] ?? "";
            if (groupBy2 && item[groupBy2]) {
                key += `^${item[groupBy2] ?? ""}`;
            }
            return key;
        });

        let results = _.map(grouped, (items, key) => {
            const keys = key.split('^');
            const result: GroupResult = {};

            result[groupBy1] = keys[0];
            if (groupBy2) result[groupBy2] = keys[1] || null;

            //Sum up $ amount fields
            result['chargeAmount'] = _.sumBy(items, 'chargeAmount');
            result['paymentTotal'] = _.sumBy(items, 'paymentTotal');
            result['insurancePaymentTotal'] = _.sumBy(items, 'insurancePaymentTotal');
            result['patientPaymentTotal'] = _.sumBy(items, 'patientPaymentTotal');
            result['adjustmentTotal'] = _.sumBy(items, 'adjustmentTotal');
            result['unappliedAmount'] = _.sumBy(items, 'unappliedAmount');
            result['refundTotal'] = _.sumBy(items, 'refundTotal');

            // Get Unique Claim Counts 
            const validClaims = _.filter(items, item => item.claimId != null);
            const uniqueClaims = _.uniqBy(validClaims, 'claimId');
            result.claimCount = uniqueClaims.length; 

            return result;
        });

        // Order the results by the group by's
        return _.orderBy(results, [groupBy1, groupBy2].filter(Boolean), ['asc', 'asc']);
    }

    function getGroupByDropDownDescription(columnName: string): string | undefined {
        const aliasInfo = reportData.advancedSearchCriteria.groupByDropDownList.find(
            (item) => item.columnName === columnName
        );
        return aliasInfo?.dropDownDescription;
    }

    async function toggleSearchCriteria() {
        reportData.showAdvancedSearchCriteria = !reportData.showAdvancedSearchCriteria;
    }

    const windowHeight = computed(() => {
        return reportData.tabName == ReportModel.FinancialTabName.AdvancedSearch ? (window.innerHeight / 16) : 33;
    });  

    async function runReport() {
        reportData.showSummaryReport = true;        
        ReportModel.FinancialReportType.Daily == props.reportType ? LoadDailyFinancialDataForSummary() : LoadMonthlyFinancialDataForSummary();
        reportData.showAdvancedSearchCriteria = false;
    }

    function formatPayments(item: any) {
        const itemValue = +item !== 0 ? (+item * -1) : +item;
        return itemValue;
    }

    function onChangeTab(selectedTab : any) {
        //reload the report on tab change
        ReportModel.FinancialReportType.Daily == props.reportType ? LoadDailyFinancialDataForSummary() : LoadMonthlyFinancialDataForSummary();
        emit("onChangeTab", selectedTab);
    }

    async function visibilityDrillDown(showDrillDown: boolean) {
        reportData.showDrillDown = showDrillDown;
    }

    async function visibilityAdvancedSearch(showAdvancedSearch: boolean) {
        reportData.showAdvancedSearchCriteria = showAdvancedSearch;
    }

</script>

<style>
    @media print {
        .no-print {
            display: none;        
        }
    }
</style>


<template>
    <PageTitle :security-screen-name="props.screenName" >
        <template #permitted>   
            <WindowHeaderFooter :headerHeight="3.5" :workareaHeight="windowHeight">
                <template v-slot:WindowHeader>
                    <div class="row mt-2" v-if="!reportData.showDrillDown">
                        <div class="col col-md-6 isalus-page-title-large pt-1">{{ reportTitle }}</div>
                        <div class="col col-md-6 text-end no-print">
                            <Tab v-if="props.reportType == ReportModel.FinancialReportType.Daily" :value="ReportModel.FinancialTabName.DailySummary" v-model="reportData.tabName" :layout="LayoutOrientation.Horz" @onChange="onChangeTab">                    
                                Daily Summary
                            </Tab>
                            <Tab v-if="props.reportType == ReportModel.FinancialReportType.Monthly" :value="ReportModel.FinancialTabName.MonthlySummary" v-model="reportData.tabName" :layout="LayoutOrientation.Horz" @onChange="onChangeTab">                    
                                Monthly Summary
                            </Tab>
                            <Tab :value="ReportModel.FinancialTabName.AdvancedSearch" v-model="reportData.tabName" :layout="LayoutOrientation.Horz" @onChange="onChangeTab">                    
                                Advanced Search
                            </Tab>
                        </div>
                    </div>
                </template>
                <template v-slot:WindowBody>  
                    <!--DAILY or MONTHLY SUMMARY TAB-->
                    <div v-if="reportData.tabName !== ReportModel.FinancialTabName.AdvancedSearch">
                        <div class="row">
                            <div class="col col-md-6 mb-3 isalus-patient-header ms-2">
                                {{ reportDescription }}<br>
                                <span class="label-isalus-small" style="font-weight: normal;">{{ monthlyReportRunDate }}</span>
                            </div>
                            <div class="col col-md-5 text-end no-print">
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.Print"
                                    :text="'Print'"
                                    :size="ButtonDisplaySize.Smaller" 
                                    :spinText="'Printing...'"
                                    :performAction="printDailySummary"
                                    :endingPadding="1"
                                />
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.Export"
                                    :text="'Export to Excel'"
                                    :size="ButtonDisplaySize.Smaller" 
                                    :spinText="'Exporting...'"
                                    :performAction="exportSummary"
                                />
                            </div>
                        </div>
                        <hr>
                        <div v-if="reportData.tabName == ReportModel.FinancialTabName.DailySummary" class="row">
                            <div class="col col-md-2 no-print" style="width: 150px;">
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.NoIcon"
                                    :text="'Today'"
                                    :tooltip="`Today's Date`" 
                                    :size="ButtonDisplaySize.Small" 
                                    :performAction="getReportToday"
                                    :endingPadding="1"
                                />
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.ChevronLeft"
                                    :text="''"
                                    :tooltip="`Subtract a day`" 
                                    :size="ButtonDisplaySize.Small" 
                                    :performAction="subtractReportDay"
                                    :endingPadding="1"
                                />
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.ChevronRight"
                                    :text="''"
                                    :tooltip="`Add a day`" 
                                    :size="ButtonDisplaySize.Small" 
                                    :performAction="addReportDay"
                                />
                            </div>
                            <div class="col col-md-2 pb-2">
                                <DatePickerNoTZ 
                                    v-model:value="reportData.reportRunDate" 
                                    :clearable="false" 
                                    :required="true" 
                                    @update:value="()=>LoadDailyFinancialDataForSummary()"
                                    :display-format="DateDisplayFormat.NumericDate"/>
                            </div>
                        </div>
                        <div v-if="reportData.tabName == ReportModel.FinancialTabName.MonthlySummary" class="row">
                            <div class="col col-md-3 no-print" style="width: 200px;">
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.NoIcon"
                                    :text="'This Month'"
                                    :tooltip="`This Month to Date`" 
                                    :size="ButtonDisplaySize.Small" 
                                    :performAction="getReportThisMonth"
                                    :endingPadding="1"
                                />
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.ChevronLeft"
                                    :text="''"
                                    :tooltip="`Subtract a month`" 
                                    :size="ButtonDisplaySize.Small" 
                                    :performAction="subtractReportMonth"
                                    :endingPadding="1"
                                />
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.ChevronRight"
                                    :text="''"
                                    :tooltip="`Add a month`" 
                                    :size="ButtonDisplaySize.Small" 
                                    :performAction="addReportMonth"
                                />
                            </div>
                            <div class="col col-md-2 pb-2">
                                <DatePickerNoTZ 
                                    v-model:value="reportData.monthlyReportStartDate" 
                                    :clearable="false" 
                                    :required="true" 
                                    :display-format="DateDisplayFormat.NumericDate"/>
                            </div>
                            <div class="col col-md-2 pb-2">
                                <DatePickerNoTZ 
                                    v-model:value="reportData.monthlyReportEndDate" 
                                    :clearable="false" 
                                    :required="true" 
                                    :display-format="DateDisplayFormat.NumericDate"/>
                            </div>
                            <div class="col col-md-2 pb-2">
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.NoIcon"
                                    :text="'Run Report'"
                                    :tooltip="`Run Monthly Report`" 
                                    :spinText="`Running Report...`"
                                    :disabled="runMonthlyReportDisabled"
                                    :size="ButtonDisplaySize.Small" 
                                    :performAction="LoadMonthlyFinancialDataForSummary"
                                />
                            </div>
                        </div>
                        <div class="row">
                            <div class="col col-md-6">
                                <SectionPanel>
                                    <i class="fa fa-solid fa-dollar-sign"></i><span class="ps-2 fw-bold">Insurance Payments</span>
                                    <div class="row">
                                        <div class="col col-md-6 fw-bold mt-3">
                                            <Currency :value="formatPayments(reportData.summaryStats.insurancePaymentAmount)"/>
                                        </div>
                                        <div class="col col-md-6 fw-bold mt-3">
                                            {{ reportData.summaryStats.insurancePaymentCount }}
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="col col-md-6">
                                            Dollar Amount
                                        </div>
                                        <div class="col col-md-6">
                                            Quantity
                                        </div>
                                    </div>
                                </SectionPanel>
                            </div>
                            <div class="col col-md-6">
                                <SectionPanel>
                                    <i class="fa fa-solid fa-money-bill-1"></i><span class="ps-2 fw-bold">Patient Payments</span>
                                    <div class="row">
                                        <div class="col col-md-6 fw-bold mt-3">
                                            <Currency :value="formatPayments(reportData.summaryStats.patientPaymentAmount)"/>
                                        </div>
                                        <div class="col col-md-6 fw-bold mt-3">
                                            {{ reportData.summaryStats.patientPaymentCount }}
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="col col-md-6">
                                            Dollar Amount
                                        </div>
                                        <div class="col col-md-6">
                                            Quantity
                                        </div>
                                    </div>
                                </SectionPanel>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col col-md-6">
                                <SectionPanel>
                                    <i class="fa fa-solid fa-folder-plus"></i><span class="ps-2 fw-bold">Claims Created</span>
                                    <div class="row">
                                        <div class="col col-md-6 fw-bold mt-3">
                                            <Currency :value="reportData.summaryStats.claimsCreatedAmount"/>
                                        </div>
                                        <div class="col col-md-6 fw-bold mt-3">
                                            {{ reportData.summaryStats.claimsCreatedCount }}
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="col col-md-6">
                                            Dollar Amount
                                        </div>
                                        <div class="col col-md-6">
                                            Quantity
                                        </div>
                                    </div>
                                </SectionPanel>
                            </div>
                            <div class="col col-md-6">
                                <SectionPanel>
                                    <i class="fa fa-solid fa-file-arrow-down"></i><span class="ps-2 fw-bold">Claims Submitted</span>
                                    <div class="row">
                                        <div class="col col-md-6 fw-bold mt-3">
                                            <Currency :value="reportData.summaryStats.claimsSubmittedAmount"/>
                                        </div>
                                        <div class="col col-md-6 fw-bold mt-3">
                                            {{ reportData.summaryStats.claimsSubmittedCount }}
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="col col-md-6">
                                            Dollar Amount
                                        </div>
                                        <div class="col col-md-6">
                                            Quantity
                                        </div>
                                    </div>
                                </SectionPanel>
                            </div>
                        </div>
                        <div class="row" v-if="props.reportType == ReportModel.FinancialReportType.Monthly">
                            <div class="col col-md-6">
                                <SectionPanel>
                                    <i class="fa fa-solid fa-xmark"></i><span class="ps-2 fw-bold">Rejected Claims</span>
                                    <div class="row">
                                        <div class="col col-md-6 fw-bold mt-3">
                                            <Currency :value="reportData.summaryStats.rejectedClaimAmount"/>                                        
                                        </div>
                                        <div class="col col-md-6 fw-bold mt-3">
                                            {{ reportData.summaryStats.rejectedClaimCount }}
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="col col-md-6">
                                            Dollar Amount
                                        </div>
                                        <div class="col col-md-6">
                                            Quantity
                                        </div>
                                    </div>
                                </SectionPanel>
                            </div>
                            <div class="col col-md-6">
                                <SectionPanel>
                                    <i class="fa fa-solid fa-dollar-sign"></i><span class="ps-2 fw-bold">Unapplied</span>
                                    <div class="row">
                                        <div class="col col-md-6 fw-bold mt-3">
                                            <Currency :value="formatPayments(reportData.summaryStats.unappliedAmount)"/>   
                                        </div>
                                        <div class="col col-md-6 fw-bold mt-3">
                                            {{ reportData.summaryStats.unappliedCount }}
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="col col-md-6">
                                            Dollar Amount
                                        </div>
                                        <div class="col col-md-6">
                                            Quantity
                                        </div>
                                    </div>
                                </SectionPanel>
                            </div>
                        </div>
                    </div>  

                    <!--ADVANCED SEARCH TAB-->
                    <div v-if="reportData.tabName == ReportModel.FinancialTabName.AdvancedSearch && !reportData.showDrillDown" class="grid-container-process label-isalus-medium">
                        <div class="pb-1 no-print">
                            <div class="row">
                                <div class="col col-md-6 mb-3 isalus-patient-header ms-2">
                                    Advanced Search<br>
                                    <span class="label-isalus-small" style="font-weight: normal;">{{ monthlyReportRunDate }}</span>
                                </div>
                            </div>
                            <div class="row">
                                <div v-if="props.reportType == ReportModel.FinancialReportType.Daily" class="col col-md-2 no-print" style="width: 150px;">
                                    <ActionButtonV2 
                                        :action="ActionButtonStyle.NoIcon"
                                        :text="'Today'"
                                        :tooltip="`Today's Date`" 
                                        :size="ButtonDisplaySize.Small" 
                                        :performAction="getReportToday"
                                        :endingPadding="1"
                                    />
                                    <ActionButtonV2 
                                        :action="ActionButtonStyle.ChevronLeft"
                                        :text="''"
                                        :tooltip="`Subtract a day`" 
                                        :size="ButtonDisplaySize.Small" 
                                        :performAction="subtractReportDay"
                                        :endingPadding="1"
                                    />
                                    <ActionButtonV2 
                                        :action="ActionButtonStyle.ChevronRight"
                                        :text="''"
                                        :tooltip="`Add a day`" 
                                        :size="ButtonDisplaySize.Small" 
                                        :performAction="addReportDay"
                                        :endingPadding="1"
                                    />    
                                </div>
                                <div v-if="props.reportType == ReportModel.FinancialReportType.Daily" class="col col-md-2 pb-2">
                                    <DatePickerNoTZ 
                                        v-model:value="reportData.reportRunDate" 
                                        :clearable="false" 
                                        :required="true" 
                                        @update:value="()=>LoadDailyFinancialDataForSummary()"
                                        :display-format="DateDisplayFormat.NumericDate"/>
                                </div>
                                <div v-if="props.reportType == ReportModel.FinancialReportType.Monthly"  class="col pb-2">
                                    <div v-if="props.reportType == ReportModel.FinancialReportType.Monthly" class="row">
                                        <div class="col col-md-3 no-print" style="width: 200px;">
                                            <ActionButtonV2 
                                                :action="ActionButtonStyle.NoIcon"
                                                :text="'This Month'"
                                                :tooltip="`This Month to Date`" 
                                                :size="ButtonDisplaySize.Small" 
                                                :performAction="getReportThisMonth"
                                                :endingPadding="1"
                                            />
                                            <ActionButtonV2 
                                                :action="ActionButtonStyle.ChevronLeft"
                                                :text="''"
                                                :tooltip="`Subtract a month`" 
                                                :size="ButtonDisplaySize.Small" 
                                                :performAction="subtractReportMonth"
                                                :endingPadding="1"
                                            />
                                            <ActionButtonV2 
                                                :action="ActionButtonStyle.ChevronRight"
                                                :text="''"
                                                :tooltip="`Add a month`" 
                                                :size="ButtonDisplaySize.Small" 
                                                :performAction="addReportMonth"
                                            />
                                        </div>
                                        <div class="col col-md-3 pb-2">
                                            <DatePickerNoTZ 
                                                v-model:value="reportData.monthlyReportStartDate" 
                                                :clearable="false" 
                                                :required="true" 
                                                :display-format="DateDisplayFormat.NumericDate"/>
                                        </div>
                                        <div class="col col-md-3 pb-2">
                                            <DatePickerNoTZ 
                                                v-model:value="reportData.monthlyReportEndDate" 
                                                :clearable="false" 
                                                :required="true" 
                                                :display-format="DateDisplayFormat.NumericDate"/>
                                        </div>
                                    </div>
                                </div>
                                <div class="col col-md-7 pb-2">
                                    <ActionButtonV2 
                                        :action="reportData.showAdvancedSearchCriteria ? ActionButtonStyle.ChveronUp : ActionButtonStyle.ChveronDown"
                                        :text="reportData.showAdvancedSearchCriteria ? 'Collapse' : 'Expand'"
                                        :tooltip="`Show/Hide Advanced Search Criteria`" 
                                        :size="ButtonDisplaySize.Smaller" 
                                        :performAction="toggleSearchCriteria"
                                    />
                                </div>
                            </div>
                            <hr>
                            <div v-if="reportData.showAdvancedSearchCriteria">
                                <div class="row">
                                    <div class="col fw-bold label-isalus-small">
                                        Date Type
                                    </div>
                                    <div class="col fw-bold label-isalus-small">
                                        Primary Payer Class
                                    </div>
                                    <div class="col fw-bold label-isalus-small">
                                        Rendering Provider
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col label-isalus-small">
                                        <v-select 
                                            v-model="reportData.advancedSearchCriteria.selectedDateType"
                                            :options="reportData.advancedSearchCriteria.dateTypeList" 
                                            :reduce="item => item.dropDownValue" label="dropDownDescription" 
                                            :select-on-tab="true" />
                                        <div v-if="!reportData.advancedSearchCriteria.selectedDateType" class="red-text mt-0 mb-1">Required</div>  
                                    </div>
                                    <div class="col label-isalus-small">
                                        <v-select 
                                            v-model="reportData.advancedSearchCriteria.selectedPrimaryPayerClassIds"
                                            :options="reportData.advancedSearchCriteria.primaryPayerClassList" 
                                            :reduce="item => +item.encode" label="decode" 
                                            :multiple="true" 
                                            :searchable="true"
                                            :filterable="true"
                                            :closeOnSelect="false"
                                            :select-on-tab="true" />
                                    </div>
                                    <div class="col label-isalus-small">
                                        <v-select 
                                            v-model="reportData.advancedSearchCriteria.selectedRenderingProviderIds"
                                            :options="reportData.advancedSearchCriteria.renderingProviderList" 
                                            :reduce="item => +item.encode" label="decode" 
                                            :multiple="true" 
                                            :searchable="true"
                                            :filterable="true"
                                            :closeOnSelect="false"
                                            :select-on-tab="true" />
                                    </div>
                                </div>
                                <div class="row mt-3">
                                    <div class="col fw-bold label-isalus-small">
                                        Group By
                                    </div>
                                    <div class="col fw-bold label-isalus-small">
                                        Primary Payer 
                                    </div>
                                    <div class="col fw-bold label-isalus-small">
                                        Location
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col label-isalus-small">
                                        <v-select                     
                                            v-model="reportData.advancedSearchCriteria.selectedGroupBy1"
                                            :options="reportData.advancedSearchCriteria.groupByDropDownList" 
                                            :reduce="item => item.columnName" label="dropDownDescription" /> 
                                        <div v-if="!reportData.advancedSearchCriteria.selectedGroupBy1" class="red-text mt-0 mb-1">Required</div>  
                                    </div>
                                    <div class="col label-isalus-small">
                                        <v-select 
                                            v-model="reportData.advancedSearchCriteria.selectedPrimaryPayerIds"
                                            :options="reportData.advancedSearchCriteria.primaryPayerList" 
                                            :reduce="item => +item.entity_id" label="PayerName" 
                                            :multiple="true" 
                                            :searchable="true"
                                            :filterable="true"
                                            :closeOnSelect="false"
                                            :select-on-tab="true" />
                                    </div>
                                    <div class="col label-isalus-small">
                                        <v-select 
                                            v-model="reportData.advancedSearchCriteria.selectedServiceLocationIds"
                                            :options="reportData.advancedSearchCriteria.serviceLocationList" 
                                            :reduce="item => +item.encode" label="decode" 
                                            :multiple="true" 
                                            :searchable="true"
                                            :filterable="true"
                                            :closeOnSelect="false"
                                            :select-on-tab="true" />
                                    </div>
                                </div>
                                <div class="row mt-3">
                                    <div class="col fw-bold label-isalus-small">
                                        SubGroup By
                                    </div>
                                    <div class="col fw-bold label-isalus-small">
                                        Post User
                                    </div>
                                    <div class="col fw-bold label-isalus-small">
                                        CPT Code
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col label-isalus-small">
                                        <v-select                     
                                            v-model="reportData.advancedSearchCriteria.selectedGroupBy2"
                                            :options="reportData.advancedSearchCriteria.groupByDropDownList" 
                                            :reduce="item => item.columnName" label="dropDownDescription" />
                                    </div>
                                    <div class="col label-isalus-small">
                                        <v-select 
                                            v-model="reportData.advancedSearchCriteria.selectedPostUserIds"
                                            :options="reportData.advancedSearchCriteria.postUserList" 
                                            :reduce="item => item.users_id" label="userText" 
                                            :multiple="true" 
                                            :searchable="true"
                                            :filterable="true"
                                            :closeOnSelect="false"
                                            :select-on-tab="true" />
                                    </div>
                                    <div class="col label-isalus-small">
                                        <v-select 
                                            v-model="reportData.advancedSearchCriteria.selectedCptCodeIds"
                                            :options="reportData.advancedSearchCriteria.cptCodeList" 
                                            :reduce="item => item.encode" label="decode" 
                                            :multiple="true" 
                                            :searchable="true"
                                            :filterable="true"
                                            :closeOnSelect="false"
                                            :select-on-tab="true" />
                                    </div>
                                </div>
                                <hr>
                                <div class="row mt-3">
                                    <div class="col fw-bold label-isalus-small">
                                        Include Refunds
                                    </div>
                                    <div class="col fw-bold label-isalus-small">
                                        Include Charges
                                    </div>
                                    <div class="col fw-bold label-isalus-small">
                                        Include Claim Count
                                    </div>
                                    <div class="col fw-bold label-isalus-small">
                                        Include Payments/Adjustments
                                    </div>
                                </div>
                                <div class="row mt-3">
                                    <div class="col label-isalus-small">
                                        <v-select 
                                            v-model="reportData.advancedSearchCriteria.selectedIncludeRefunds"
                                            :options="reportData.advancedSearchCriteria.booleanList" 
                                            :reduce="item => item.booleanValue" label="dropDownDescription" 
                                            :select-on-tab="true" />
                                        <div v-if="undefined == reportData.advancedSearchCriteria.selectedIncludeRefunds" class="red-text mt-0 mb-1">Required</div> 
                                    </div>
                                    <div class="col label-isalus-small">
                                        <v-select 
                                            v-model="reportData.advancedSearchCriteria.selectedIncludeCharges"
                                            :options="reportData.advancedSearchCriteria.booleanList" 
                                            :reduce="item => item.booleanValue" label="dropDownDescription" 
                                            :select-on-tab="true" />
                                        <div v-if="undefined == reportData.advancedSearchCriteria.selectedIncludeCharges" class="red-text mt-0 mb-1">Required</div> 
                                    </div>
                                    <div class="col label-isalus-small">
                                        <v-select 
                                            v-model="reportData.advancedSearchCriteria.selectedIncludeCounts"
                                            :options="reportData.advancedSearchCriteria.booleanList" 
                                            :reduce="item => item.booleanValue" label="dropDownDescription" 
                                            :select-on-tab="true" />
                                        <div v-if="undefined == reportData.advancedSearchCriteria.selectedIncludeCounts" class="red-text mt-0 mb-1">Required</div>
                                    </div>
                                    <div class="col label-isalus-small">
                                        <v-select 
                                            v-model="reportData.advancedSearchCriteria.selectedIncludePaymentsAdjustments"
                                            :options="reportData.advancedSearchCriteria.booleanList" 
                                            :reduce="item => item.booleanValue" label="dropDownDescription" 
                                            :select-on-tab="true" />
                                        <div v-if="undefined == reportData.advancedSearchCriteria.selectedIncludePaymentsAdjustments" class="red-text mt-0 mb-1">Required</div>
                                    </div>
                                </div>
                                <div class="pt-3 pb-1">
                                    <ActionButtonV2
                                        :action="ActionButtonStyle.NoIcon"
                                        :disabled="runReportDisabled"
                                        :text="`Run Report`"
                                        :spinText="`Running Report...`"
                                        :size="ButtonDisplaySize.Small" 
                                        :performAction="runReport" />
                                </div>
                                <hr>
                            </div>                            
                        </div> 
                    </div>

                    <!--MAIN REPORT SECTION ON ADV SEARCH TAB FOR DAILY-->
                    <div v-if="reportData.showSummaryReport && (reportData.tabName == ReportModel.FinancialTabName.AdvancedSearch) && (props.reportType == ReportModel.FinancialReportType.Daily)" class="container-fluid pt-1">
                        <FinancialReportDailySummary
                            :screenName="props.screenName"
                            :reportRunDate="reportData.reportRunDate ?? getNoTzToday()"
                            :selectedDateType="reportData.advancedSearchCriteria.selectedDateType"
                            :summarizedReportDataDaily="reportData.summarizedReportData"
                            :selectedGroupBy1="reportData.advancedSearchCriteria.selectedGroupBy1"
                            :selectedGroupBy2="reportData.advancedSearchCriteria.selectedGroupBy2"
                            :selectedIncludeCounts="reportData.advancedSearchCriteria.selectedIncludeCounts"
                            :selectedIncludeCharges="reportData.advancedSearchCriteria.selectedIncludeCharges"
                            :selectedIncludePaymentsAdjustments="reportData.advancedSearchCriteria.selectedIncludePaymentsAdjustments"
                            :selectedIncludeRefunds="reportData.advancedSearchCriteria.selectedIncludeRefunds"
                            :totalFields="reportData.totalFields"
                            :groupByDropDownList="reportData.advancedSearchCriteria.groupByDropDownList"
                            :mainReportData="reportData.mainReportData"
                            @onShowDrillDown="visibilityDrillDown" 
                            @onShowAdvancedSearchCriteria="visibilityAdvancedSearch"
                        />
                    </div>

                    <!--MAIN REPORT SECTION ON ADV SEARCH TAB FOR MONTHLY-->
                    <div v-if="reportData.showSummaryReport && (reportData.tabName == ReportModel.FinancialTabName.AdvancedSearch) && (props.reportType == ReportModel.FinancialReportType.Monthly)" class="container-fluid pt-1">
                        <FinancialReportMonthlySummary
                            :screenName="props.screenName"
                            :monthlyReportStartDate="reportData.monthlyReportStartDate!" 
                            :monthlyReportEndDate="reportData.monthlyReportEndDate!"
                            :selectedDateType="reportData.advancedSearchCriteria.selectedDateType"
                            :summarizedReportDataMonthly="ReportModel.FinancialReportDateTypes.SubmissionDate == reportData.advancedSearchCriteria.selectedDateType ? reportData.submittedClaimsDataMonthly : reportData.mainDataMonthly"
                            :selectedGroupBy1="reportData.advancedSearchCriteria.selectedGroupBy1"
                            :selectedGroupBy2="reportData.advancedSearchCriteria.selectedGroupBy2"
                            :selectedIncludeCounts="reportData.advancedSearchCriteria.selectedIncludeCounts"
                            :selectedIncludeCharges="reportData.advancedSearchCriteria.selectedIncludeCharges"
                            :selectedIncludePaymentsAdjustments="reportData.advancedSearchCriteria.selectedIncludePaymentsAdjustments"
                            :selectedIncludeRefunds="reportData.advancedSearchCriteria.selectedIncludeRefunds"
                            :groupByDropDownList="reportData.advancedSearchCriteria.groupByDropDownList"
                            @onShowDrillDown="visibilityDrillDown" 
                            @onShowAdvancedSearchCriteria="visibilityAdvancedSearch"
                        />
                    </div>
                </template>
            </WindowHeaderFooter> 
        </template>
    </PageTitle>
</template>
